// src/components/EditDoctorModal.jsx
import React, { useEffect, useState, useRef } from "react";
import CloseIcon from "../../../assets/crossIcon.svg";
import Select from "react-select";
import { CircularProgress } from "@mui/material";
import { HospitalServices } from "../../../services/HospitalServices";
import { AuthServices } from "../../../services/AuthServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

const doctorTypeOption = [
  { value: "Duty Doctor", label: "Duty Doctor" },
  { value: "Specialist", label: "Specialist" },
  { value: "Paramedic", label: "Paramedic" },
  { value: "Anaesthist", label: "Anaesthist" },
  { value: "Consultant", label: "Consultant" },
  { value: "Visiting Consultant", label: "Visiting Consultant" },
  { value: "Others", label: "Others" },
];

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const statusOptions = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

const EditDoctorModal = ({ open, onClose, doctor }) => {
  const [specialityList, setSpecialityList] = useState([]);
  const [hospitalList, setHospitalList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const [form, setForm] = useState({
    fullName: null,
    hprId: null,
    doctorType: null,
    email: null,
    phone: null,
    gender: null,
    birthDate: null,
    country: null,
    state: null,
    city: null,
    address: null,
    pincode: null,
    highestQualification: null,
    hospitalId: null,
    specialityIds: [],
    fromDate: null,
    registrationCertificate: null,
    existingCertificateUrl: null,
    status: "ACTIVE",
  });

  useEffect(() => {
    if (doctor) {
      const normalizedGender = doctor.gender
        ? doctor.gender.trim().charAt(0).toUpperCase() +
          doctor.gender.trim().slice(1).toLowerCase()
        : "";

      setForm({
        fullName: doctor.fullName || null,
        hprId: doctor.hprId || null,
        doctorType: doctor.doctorType || null,
        email: doctor.email || null,
        phone: doctor.phone || null,
        gender: normalizedGender,
        birthDate: doctor.birthDate ? doctor.birthDate.slice(0, 10) : null,
        country: doctor.country || null,
        state: doctor.state || null,
        city: doctor.city || null,
        address: doctor.address || null,
        pincode: doctor.pincode || null,
        highestQualification: doctor.highestQualification || null,
        hospitalId: doctor?.doctorhospitalmappings?.length
          ? doctor?.doctorhospitalmappings[0]?.hospitalId
          : null,
        specialityIds: doctor.specialityIds || [],
        fromDate: doctor.fromDate || null,
        registrationCertificate: null,
        existingCertificateUrl: doctor.certificateUrl || null,
        status: doctor.status || "ACTIVE",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  }, [doctor]);

  const loadData = async () => {
    try {
      const [specialitiesRes, hospitalsRes] = await Promise.all([
        HospitalServices.getUniqueSpecialityList("", 1, 200),
        HospitalServices.getHospitalList("", 1, 200),
      ]);
      setSpecialityList(specialitiesRes?.data?.list ?? []);
      setHospitalList(hospitalsRes?.data?.list ?? []);
    } catch (err) {
      handleApiErrorResponses(err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Upload file and update existingCertificateUrl in form
  const uploadImageHandler = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5242880) {
      window.alert("Please upload a file smaller than 5 MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await AuthServices.uploadImage(formData);
      setForm((p) => ({
        ...p,
        existingCertificateUrl: res.data.url,
        registrationCertificate: null,
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setUploading(false); // And this line
    }
  };
  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = { ...form };
      // Always send URL string for certificateUrl
      payload.certificateUrl = form.existingCertificateUrl || "";

      delete payload.registrationCertificate;
      delete payload.existingCertificateUrl;

      await HospitalServices.updateDoctorDetail(doctor.id, payload);
      window.alert("Doctor details updated successfully");
      onClose(true);
    } catch (err) {
      handleApiErrorResponses(err);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const toSelectValues = (ids, list) =>
    list
      ? list
          .filter((i) => ids?.includes(i.specialityId))
          .map((i) => ({ value: i.specialityId, label: i.speciality }))
      : [];

  const fromSelectValues = (items) => items.map((i) => i.value);

  if (!open) return null;

  // console.log("hospital list:", hospitalList);
  console.log("doctor hospital:", form.hospitalId);
  console.log("doctor:", doctor);

  return (
    <div className="fixed top-0 left-0 w-screen h-screen bg-[#00000066] flex items-center justify-center z-[1200]">
      <div className="relative w-[95%] max-w-[1000px] bg-white rounded-[16px] p-6 overflow-y-auto max-h-[95vh]">
        <div
          className="absolute top-4 right-4 cursor-pointer"
          onClick={() => onClose(false)}
        >
          <img src={CloseIcon} alt="close" />
        </div>

        <div className="text-xl font-semibold text-[#191919] mb-4">
          Edit Doctor
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Full Name */}
          <label className="label1">
            <div className="text-content">Full Name *</div>
            <input
              className="input-field"
              value={form.fullName}
              onChange={(e) =>
                setForm((p) => ({ ...p, fullName: e.target.value || null }))
              }
              placeholder="Enter Full Name"
            />
          </label>

          {/* Registration Number */}
          <label className="label1">
            <div className="text-content">Registration Number</div>
            <input
              className="input-field"
              value={form.hprId}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  hprId: e.target.value || null,
                }))
              }
              placeholder="Enter Registration Number"
            />
          </label>

          {/* Doctor Type */}
          <label className="label1">
            <div className="text-content">Doctor Type *</div>
            <Select
              options={doctorTypeOption}
              value={doctorTypeOption.find((x) => x.value === form.doctorType)}
              onChange={(item) =>
                setForm((p) => ({ ...p, doctorType: item?.value || null }))
              }
              placeholder="Select Doctor Type"
            />
          </label>

          {/* Email */}
          <label className="label1">
            <div className="text-content">Email</div>
            <input
              className="input-field"
              value={form.email}
              onChange={(e) =>
                setForm((p) => ({ ...p, email: e.target.value || null }))
              }
              placeholder="Enter Email"
            />
          </label>

          {/* Phone */}
          <label className="label1">
            <div className="text-content">Phone</div>
            <input
              className="input-field"
              value={form.phone}
              onChange={(e) =>
                setForm((p) => ({ ...p, phone: e.target.value || null }))
              }
              placeholder="Enter Phone"
              maxLength={10}
            />
          </label>

          {/* Gender */}
          <label className="label1">
            <div className="text-content">Gender *</div>
            <Select
              options={genderOptions}
              value={genderOptions.find((x) => x.value === form.gender)}
              onChange={(item) =>
                setForm((p) => ({ ...p, gender: item?.value || null }))
              }
              placeholder="Select Gender"
            />
          </label>

          {/* Date of Birth */}
          <label className="label1">
            <div className="text-content">Date of Birth</div>
            <input
              className="input-field"
              type="date"
              value={form.birthDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, birthDate: e.target.value || null }))
              }
            />
          </label>

          {/* Country */}
          <label className="label1">
            <div className="text-content">Country</div>
            <input
              className="input-field"
              value={form.country}
              onChange={(e) =>
                setForm((p) => ({ ...p, country: e.target.value || null }))
              }
              placeholder="Select Country"
            />
          </label>

          {/* State */}
          <label className="label1">
            <div className="text-content">State</div>
            <input
              className="input-field"
              value={form.state}
              onChange={(e) =>
                setForm((p) => ({ ...p, state: e.target.value || null }))
              }
              placeholder="Select State"
            />
          </label>

          {/* City */}
          <label className="label1">
            <div className="text-content">City</div>
            <input
              className="input-field"
              value={form.city}
              onChange={(e) =>
                setForm((p) => ({ ...p, city: e.target.value || null }))
              }
              placeholder="Select City"
            />
          </label>

          {/* Address Line 1 */}
          <label className="label1">
            <div className="text-content">Address Line 1</div>
            <input
              className="input-field"
              value={form.address}
              onChange={(e) =>
                setForm((p) => ({ ...p, address: e.target.value || null }))
              }
              placeholder="Address Line 1"
            />
          </label>

          {/* Pincode */}
          <label className="label1">
            <div className="text-content">Pincode</div>
            <input
              className="input-field"
              value={form.pincode}
              onChange={(e) =>
                setForm((p) => ({ ...p, pincode: e.target.value || null }))
              }
              placeholder="Enter Pincode"
            />
          </label>

          {/* Highest Qualification */}
          <label className="label1">
            <div className="text-content">Highest Qualification</div>
            <input
              className="input-field"
              value={form.highestQualification}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  highestQualification: e.target.value || null,
                }))
              }
              placeholder="Highest Qualification"
            />
          </label>

          {/* Hospital Name */}
          <label className="label1">
            <div className="text-content">Hospital Name *</div>
            <Select
              options={hospitalList}
              getOptionLabel={(options) => options.name}
              getOptionValue={(options) => options.id}
              value={hospitalList.filter((item) => item.id === form.hospitalId)}
              onChange={(item) =>
                setForm((p) => ({ ...p, hospitalId: item?.id || null }))
              }
              placeholder="Select Hospital"
            />
          </label>

          {/* Speciality */}
          {specialityList.length > 0 && (
            <label className="label1">
              <div className="text-content">Speciality</div>
              <Select
                isMulti
                options={specialityList.map((i) => ({
                  value: i.specialityId,
                  label: i.speciality,
                }))}
                value={toSelectValues(form.specialityIds, specialityList)}
                onChange={(items) =>
                  setForm((p) => ({
                    ...p,
                    specialityIds: fromSelectValues(items),
                  }))
                }
                placeholder="Select Speciality"
              />
            </label>
          )}

          {/* From Date */}
          <label className="label1">
            <div className="text-content">From Date</div>
            <input
              className="input-field"
              type="date"
              value={form.fromDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, fromDate: e.target.value || null }))
              }
            />
          </label>

          {/* Registration Certificate */}
          <label className="label1">
            <div className="text-content">Registration Certificate</div>
            {form.existingCertificateUrl && (
              <div className="mb-2">
                <a
                  href={form.existingCertificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-blue-600"
                >
                  View Current Certificate
                </a>
              </div>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={uploadImageHandler}
              accept=".jpg,.jpeg,.png"
            />
          </label>

          {/* Status */}
          <label className="label1">
            <div className="text-content">Status</div>
            <Select
              options={statusOptions}
              value={statusOptions.find((x) => x.value === form.status)}
              onChange={(item) =>
                setForm((p) => ({ ...p, status: item?.value || "ACTIVE" }))
              }
              placeholder="Select Status"
            />
          </label>
        </div>

        <div className="flex justify-center mt-8">
          {saving ? (
            <CircularProgress />
          ) : (
            <button
              onClick={handleSave}
              className="text-sm text-white bg-[#017C57] font-semibold rounded-full px-8 py-2"
              disabled={saving || uploading}
            >
              Save Details
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditDoctorModal;
